/**
 * useIdleTimer — fire onLock after `lockAfterMs` of inactivity and onPause after
 * `pauseAfterMs`. Each callback fires once per idle stretch; any user activity
 * (mouse/keyboard/touch/scroll) resets both — but only while `trackActivity` is
 * true. The provider sets trackActivity=false once locked so the pause timer keeps
 * climbing toward 5 min even though the screen is already locked.
 *
 * Returns { reset } so the caller can restart the cycle after an unlock.
 */

import { useCallback, useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'click'];

export default function useIdleTimer({
  lockAfterMs = 180000,   // 3 min
  pauseAfterMs = 300000,  // 5 min
  trackActivity = true,
  checkIntervalMs = 1000,
  enabled = true,
  onLock,
  onPause,
}) {
  const lastActivityRef = useRef(Date.now());
  const firedLockRef = useRef(false);
  const firedPauseRef = useRef(false);
  const trackRef = useRef(trackActivity);
  const onLockRef = useRef(onLock);
  const onPauseRef = useRef(onPause);

  trackRef.current = trackActivity;
  onLockRef.current = onLock;
  onPauseRef.current = onPause;

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    firedLockRef.current = false;
    firedPauseRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const handleActivity = () => {
      if (!trackRef.current) return;
      lastActivityRef.current = Date.now();
      firedLockRef.current = false;
      firedPauseRef.current = false;
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));

    const intervalId = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (!firedLockRef.current && idleMs >= lockAfterMs) {
        firedLockRef.current = true;
        if (onLockRef.current) onLockRef.current();
      }
      if (!firedPauseRef.current && idleMs >= pauseAfterMs) {
        firedPauseRef.current = true;
        if (onPauseRef.current) onPauseRef.current();
      }
    }, checkIntervalMs);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
    };
  }, [lockAfterMs, pauseAfterMs, checkIntervalMs, enabled]);

  return { reset };
}
